<template>
    <div class="flex-1">
        <skeleton v-if="planBookingPage === undefined" />
        <not-found v-else-if="planBookingPage === null" />
        <not-found
            v-else-if="planBookingPage.__typename === 'InvalidBookingPage'"
            :title="planBookingPage.title"
            :reason="planBookingPage.reason"
        />
        <not-found
            v-else-if="!planBookingPage.active"
            :reason="t('pages.inactivePlan')"
        />
        <template v-else>
            <purchase-recurring-program
                v-if="isRecurring"
                v-model:contract="recurringContractData"
                :plan="planBookingPage"
                :is-editing="false"
            />
            <purchase-program
                v-else
                v-model:contract="contractData"
                :plan="planBookingPage"
                :is-editing="false"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import Skeleton from 'primevue/skeleton'
import { useRoute } from '#app'
import { computed, ref } from 'vue'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { type FragmentType, graphql } from '~/resources/graphql'
import { useSelectedLanguage } from '~/composables/use-selected-language'
import PurchaseProgram, {
    PurchaseProgramContractFragment
} from '~/components/PurchaseContract/Program/PurchaseProgram.vue'
import PurchaseRecurringProgram, {
    PurchaseRecurringProgramContractFragment
} from '~/components/PurchaseContract/Program/PurchaseRecurringProgram.vue'
import NotFound from '~/components/NotFound/NotFound.vue'
import { useI18n } from 'vue-i18n'

const route = useRoute()
const slug = computed(() => route.params.slug)
const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })

const query = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const { data } = useQuery({
    queryKey: ['ProgramsPageQuery', slug, activeFacility, selectedLanguage],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query ProgramsPageQuery(
                    $bookingInput: PlanBookingPageInput!
                    $input: SimpleSpokeInput
                    $language: Language!
                ) {
                    planBookingPage(input: $bookingInput) {
                        __typename
                        ... on InvalidBookingPage {
                            title
                            reason
                        }
                        ... on Plan {
                            ...PurchaseProgramPlanFragment
                            ...PurchaseRecurringProgramPlanFragment
                            active
                            terms {
                                billingType
                            }
                        }
                    }
                }
            `),
            {
                facilityId: activeFacility.value.id,
                input: {
                    facilityId: activeFacility.value.id
                },
                language: selectedLanguage.value,
                bookingInput: {
                    slug: slug.value,
                    facilityId: activeFacility.value.id,
                    planType: 'PROGRAM'
                }
            }
        )
})

const contractData = ref<
    FragmentType<typeof PurchaseProgramContractFragment> | undefined
>()
const recurringContractData =
    ref<FragmentType<typeof PurchaseRecurringProgramContractFragment>>()

const planBookingPage = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    if (data.value.planBookingPage === null) {
        return null
    }

    return data.value.planBookingPage
})

const isRecurring = computed(() => {
    if (
        planBookingPage.value === undefined ||
        planBookingPage.value === null ||
        planBookingPage.value.__typename !== 'Plan'
    ) {
        throw new Error()
    }

    return planBookingPage.value.terms.billingType === 'RECURRING'
})
</script>
